import { useEffect, useState } from 'react';
import type { SyntheticEvent } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';

type UseChannelInfoTooltipOptions = {
  /**
   * When true and the pointer is coarse, the trigger tap calls
   * `event.stopPropagation()` so it never reaches an enclosing selectable card's
   * onClick. Selection-aware sites (IndividualChannelCard) pass this; the
   * non-selectable TVChannelLogos site leaves it off.
   */
  stopPropagation?: boolean;
};

/**
 * The subset of MUI `Tooltip` props the hook drives. On a coarse pointer it
 * returns a controlled `open` + `onClose`; on a fine pointer `open` is
 * `undefined`, leaving the Tooltip in its default uncontrolled hover/focus mode.
 */
type ChannelInfoTooltipProps = {
  open?: boolean;
  onClose?: (event: SyntheticEvent | Event) => void;
};

type UseChannelInfoTooltipReturn = {
  isCoarsePointer: boolean;
  tooltipProps: ChannelInfoTooltipProps;
  onTriggerTap: (event: SyntheticEvent) => void;
};

/**
 * R16 — makes the channel info-card reachable on touch devices.
 *
 * On a coarse pointer (`(hover: none)`), the channel hover-card has no hover to
 * open it, so this hook flips the Tooltip into controlled mode: the trigger's
 * tap opens it, and a tap-outside or Escape closes it (both handled internally,
 * so call sites need no ClickAwayListener). On a fine pointer it returns a
 * passthrough — no `open` control — preserving the existing desktop hover/focus
 * behaviour exactly.
 *
 * Shared across every channel-info site so the behaviour lives in one place.
 */
export const useChannelInfoTooltip = ({
  stopPropagation = false,
}: UseChannelInfoTooltipOptions = {}): UseChannelInfoTooltipReturn => {
  const isCoarsePointer = useMediaQuery('(hover: none)');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isCoarsePointer || !open) return;

    const close = () => setOpen(false);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCoarsePointer, open]);

  const onTriggerTap = (event: SyntheticEvent): void => {
    if (!isCoarsePointer) return;
    if (stopPropagation) event.stopPropagation();
    setOpen(true);
  };

  const handleClose = (): void => setOpen(false);

  const tooltipProps: ChannelInfoTooltipProps = isCoarsePointer
    ? { open, onClose: handleClose }
    : {};

  return { isCoarsePointer, tooltipProps, onTriggerTap };
};
